import React from 'react';

interface UIContextProps {
  loadedSearch: string | null;
  setLoadedSearch: (search: string | null) => void;
  targetSearch: string | null;
  targetTags: string[];
  loadedTags: string[];
  setLoadedTags: (tags: string[]) => void;
  targetNotConsistent: () => boolean;
  setTargetFromUrlParams: (searchParams: URLSearchParams) => void;
  loadingBookmarks: boolean;
  setLoadingBookmarks: (loading: boolean) => void;
  saving: boolean;
  setSaving: (saving: boolean) => void;
}

const UIContext = React.createContext<UIContextProps>({
  loadedSearch: null,
  setLoadedSearch: () => { },
  targetSearch: null,
  targetTags: [],
  loadedTags: [],
  setLoadedTags: () => { },
  targetNotConsistent: () => false,
  setTargetFromUrlParams: () => { },
  loadingBookmarks: false,
  setLoadingBookmarks: () => { },
  saving: false,
  setSaving: () => { }
});

export const useUIContext = (): UIContextProps =>
  React.useContext(UIContext);

interface UIProviderProps {
  children: React.ReactNode;
}

export const UIProvider: React.FC<UIProviderProps> = ({
  children,
}: UIProviderProps) => {
  const [loadedSearch, setLoadedSearch] = React.useState<string | null>(null);
  const [targetSearch, setTargetSearch] = React.useState<string | null>(null);
  const [targetTags, setTargetTags] = React.useState<string[]>([]);
  const [loadedTags, setLoadedTags] = React.useState<string[]>([]);
  const [loadingBookmarks, setLoadingBookmarks] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const setTargetFromUrlParams = (searchParams: URLSearchParams) => {
    if (searchParams.get('search')) {
      setTargetSearch(searchParams.get('search') || '');
    }
    else {
      setTargetSearch(null);
    }
    const tagsParam = searchParams.get('tags');
    const newTags = tagsParam ? tagsParam.split(',').map(t => t.trim()).filter(t => t.length > 0) : [];
    setTargetTags(prev => {
      if (prev.join(',') === newTags.join(',')) return prev;
      return newTags;
    });
  };

  return (
    <UIContext.Provider value={{
      loadedSearch,
      setLoadedSearch,
      targetSearch,
      targetTags,
      loadedTags,
      setLoadedTags,
      targetNotConsistent: () => (targetSearch !== loadedSearch || targetTags.join(',') !== loadedTags.join(',')),
      setTargetFromUrlParams,
      loadingBookmarks,
      setLoadingBookmarks,
      saving,
      setSaving
    }}>
      {children}
    </UIContext.Provider>
  );
};
